import { useState, KeyboardEvent, ClipboardEvent, FocusEvent } from "react";
import { cn } from "@/lib/utils";

interface TagsInputProps {
  initialTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  validateValue?: (value: string) => boolean;
  placeholder?: string;
  appearance?: "default" | "profile";
  inputId?: string;
}

function TagsInput({
  placeholder = "Type something",
  initialTags = [],
  onTagsChange,
  validateValue,
  appearance = "default",
  inputId,
}: TagsInputProps) {
  const [tags, setTags] = useState<string[]>(initialTags);
  const isProfile = appearance === "profile";

  function addTag(value: string) {
    if (!value.trim()) {
      return;
    }
    if (validateValue && !validateValue(value)) {
      return;
    }
    const newTags = [...tags, value];
    setTags(newTags);
    onTagsChange?.(newTags);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!["Space", "Tab", "Enter"].includes(e.code)) {
      return;
    }
    if (e.code === "Enter") {
      e.stopPropagation();
      e.preventDefault();
    }
    const value = e.currentTarget.value;
    addTag(value);
    e.currentTarget.value = "";
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const values = pastedText.split(/[,\s]+/).filter(Boolean);

    const newTags = [...tags];
    values.forEach((value) => {
      if (!value.trim()) {
        return;
      }
      if (validateValue && !validateValue(value)) {
        return;
      }
      newTags.push(value);
    });

    setTags(newTags);
    onTagsChange?.(newTags);
    e.currentTarget.value = "";
  }

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    const value = e.currentTarget.value;
    if (value) {
      addTag(value);
      e.currentTarget.value = "";
    }
  }

  function removeTag(index: number) {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
    onTagsChange?.(newTags);
  }

  return (
    <div
      style={
        isProfile
          ? undefined
          : {
              padding: ".5em",
              borderRadius: "3px",
              marginTop: "1em",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: ".5em",
            }
      }
      className={cn(
        "border border-gray-300",
        isProfile &&
          "flex min-h-11 flex-wrap items-center gap-2 rounded-xl border-slate-200 bg-slate-50 px-3.5 py-2 text-sm transition-colors focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100",
      )}
    >
      {tags.map((tag, index) => (
        <div
          style={
            isProfile
              ? undefined
              : {
                  backgroundColor: "rgb(218, 216, 216)",
                  display: "inline-block",
                  padding: ".5em .75em",
                  borderRadius: "20px",
                }
          }
          key={index}
          className={cn(
            isProfile &&
              "inline-flex max-w-full items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700",
          )}
        >
          <span className={cn(isProfile && "truncate")}>{tag}</span>
          <button
            type="button"
            aria-label={`Remove ${tag}`}
            onClick={() => removeTag(index)}
            className={cn(
              isProfile &&
                "inline-flex size-5 shrink-0 items-center justify-center rounded-md text-base text-blue-500 hover:bg-blue-100 hover:text-blue-800 focus-visible:outline-2 focus-visible:outline-blue-600",
            )}
            style={
              isProfile
                ? undefined
                : {
                    height: "20px",
                    width: "20px",
                    backgroundColor: "rgb(48, 48, 48)",
                    color: "#fff",
                    borderRadius: "50%",
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginLeft: ".5em",
                    fontSize: "18px",
                    cursor: "pointer",
                  }
            }
          >
            &times;
          </button>
        </div>
      ))}
      <input
        id={inputId}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        type="text"
        className={cn(
          isProfile &&
            "min-w-0 flex-1 basis-40 border-0 bg-transparent py-1 text-base text-slate-900 outline-hidden placeholder:text-slate-400 sm:text-sm",
        )}
        style={
          isProfile
            ? undefined
            : {
                flexGrow: 1,
                padding: ".5em 0",
                border: "none",
                outline: "none",
              }
        }
        placeholder={placeholder}
      />
    </div>
  );
}

export default TagsInput;
